import { z } from "zod";

import { MAX_SYMBOLS } from "./snapshotStream.constants";

export type LiveQuote = { price?: number; previousClose?: number };

export interface SnapshotStream {
  start(): void;
  stop(): void;
  setSymbols(symbols: string[]): void;
  onQuotes(handler: (quotes: Record<string, LiveQuote>) => void): () => void;
}

const RECONNECT_DELAYS_MS = [500, 1_000, 2_000, 5_000];

const AUTH_ERROR_CODES = new Set(["unauthorized", "forbidden", "entitlement"]);

export function tradingApiWsUrl(
  path: string,
  location: {
    TRADING_API_BASE_URL: string;
    protocol: string;
    host: string;
  } = {
    TRADING_API_BASE_URL: window.env?.TRADING_API_BASE_URL ?? "",
    protocol: window.location.protocol,
    host: window.location.host,
  },
): string {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const base = location.TRADING_API_BASE_URL.replace(/\/$/, "");

  if (/^https?:\/\//i.test(base)) {
    return `${base.replace(/^http/i, "ws")}${normalizedPath}`;
  }

  const protocol = location.protocol === "https:" ? "wss:" : "ws:";
  const prefix = base.startsWith("/") || base === "" ? base : `/${base}`;
  return `${protocol}//${location.host}${prefix}${normalizedPath}`;
}

const capSymbols = (symbols: string[]): string[] =>
  [...new Set(symbols)].slice(0, MAX_SYMBOLS);

const snapshotItemSchema = z.object({
  S: z.string(),
  p: z.number().optional(),
  c: z.number().optional(),
  pc: z.number().optional(),
  t: z.string().optional(),
});

const inboundMessageSchema = z.discriminatedUnion("T", [
  z.object({ T: z.literal("connected"), slice: z.string() }),
  z.object({ T: z.literal("subscription"), symbols: z.array(z.string()) }),
  z.object({
    T: z.literal("snapshots"),
    items: z.array(snapshotItemSchema),
  }),
  z.object({ T: z.literal("error"), code: z.string(), msg: z.string() }),
  z.object({ T: z.literal("pong") }),
]);

type InboundMessage = z.infer<typeof inboundMessageSchema>;
type SnapshotItem = z.infer<typeof snapshotItemSchema>;

const parseInbound = (raw: string): InboundMessage | undefined => {
  try {
    const parsed = inboundMessageSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : undefined;
  } catch {
    return undefined;
  }
};

const mapSnapshotQuotes = (
  items: SnapshotItem[],
): Record<string, LiveQuote> => {
  const quotes: Record<string, LiveQuote> = {};
  for (const item of items) {
    const quote: LiveQuote = {};
    if (item.p !== undefined) {
      quote.price = item.p;
    }
    if (item.pc !== undefined) {
      quote.previousClose = item.pc;
    }
    quotes[item.S] = quote;
  }
  return quotes;
};

export function createSnapshotStream(opts: {
  getToken: () => Promise<string>;
  webSocketURL: string;
  webSocket?: typeof WebSocket;
}): SnapshotStream {
  const WebSocketImpl = opts.webSocket ?? WebSocket;
  const quoteHandlers = new Set<(quotes: Record<string, LiveQuote>) => void>();

  let socket: WebSocket | null = null;
  let desired = new Set<string>();
  let subscribed = new Set<string>();
  let started = false;
  let stopped = true;
  let connected = false;
  let reconnectAttempt = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  const send = (body: unknown) => {
    if (socket?.readyState === WebSocketImpl.OPEN) {
      socket.send(JSON.stringify(body));
    }
  };

  const applyDiff = () => {
    if (!connected) {
      return;
    }

    const added = desired.difference(subscribed);
    const removed = subscribed.difference(desired);

    if (added.size > 0) {
      send({ action: "subscribe", symbols: [...added] });
    }
    if (removed.size > 0) {
      send({ action: "unsubscribe", symbols: [...removed] });
    }

    subscribed = new Set(desired);
  };

  const handleMessage = (raw: string) => {
    const message = parseInbound(raw);
    if (!message) {
      return;
    }

    switch (message.T) {
      case "connected": {
        connected = true;
        subscribed = new Set();
        reconnectAttempt = 0;
        applyDiff();
        return;
      }
      case "subscription": {
        subscribed = new Set(message.symbols);
        return;
      }
      case "snapshots": {
        const quotes = mapSnapshotQuotes(message.items);
        for (const handler of quoteHandlers) {
          handler(quotes);
        }
        return;
      }
      case "error": {
        // A non-auth error rejects one request, not the connection, so the
        // socket stays usable and its state must survive.
        if (!AUTH_ERROR_CODES.has(message.code)) {
          return;
        }
        connected = false;
        subscribed = new Set();
        if (socket?.readyState === WebSocketImpl.OPEN) {
          socket.close();
        }
        return;
      }
      case "pong":
        return;
      default: {
        message satisfies never;
      }
    }
  };

  const clearReconnect = () => {
    if (reconnectTimer !== null) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  };

  const detach = (next: WebSocket | null) => {
    if (socket && socket !== next) {
      socket.onclose = null;
      socket.onmessage = null;
      socket.onopen = null;
      if (socket.readyState === WebSocketImpl.OPEN) {
        socket.close();
      }
    }
    socket = next;
  };

  const scheduleReconnect = () => {
    if (stopped) {
      return;
    }
    const delay =
      RECONNECT_DELAYS_MS[
        Math.min(reconnectAttempt, RECONNECT_DELAYS_MS.length - 1)
      ];
    reconnectAttempt += 1;
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      if (!stopped) {
        connect();
      }
    }, delay);
  };

  const connect = () => {
    const next = new WebSocketImpl(opts.webSocketURL);
    detach(next);

    next.onopen = () => {
      void opts
        .getToken()
        .then((token) => {
          if (socket === next && next.readyState === WebSocketImpl.OPEN) {
            next.send(JSON.stringify({ action: "auth", token }));
          }
        })
        .catch(() => {
          if (socket === next) {
            next.close();
          }
        });
    };

    next.onmessage = (event) => {
      handleMessage(String((event as MessageEvent).data));
    };

    next.onclose = () => {
      connected = false;
      subscribed = new Set();
      if (socket === next) {
        socket = null;
      }
      scheduleReconnect();
    };
  };

  return {
    start() {
      if (started) {
        return;
      }
      started = true;
      stopped = false;
      reconnectAttempt = 0;
      connect();
    },
    stop() {
      stopped = true;
      started = false;
      connected = false;
      clearReconnect();
      detach(null);
    },
    setSymbols(symbols: string[]) {
      desired = new Set(capSymbols(symbols));
      applyDiff();
    },
    onQuotes(handler) {
      quoteHandlers.add(handler);
      return () => {
        quoteHandlers.delete(handler);
      };
    },
  };
}
